import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../css/MainGame.css";
import "../../css/Font-Awesome.min.css";
import { SendRequest } from "../../util/AxiosUtil";
import Player from "../../components/MainGameComponents/Player";

import PotDiv from "../../components/MainGameComponents/PotDiv";
import CurrentPlayerDiv from "../../components/MainGameComponents/CurrentPlayer";
import DealerPanel from "../../components/MainGameComponents/DealerPanel";
import GameControlPanel from "../../components/MainGameComponents/GameControlPanel";
import LogRocket from "../../util/LogRocketUtil";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MeetingProvider,
  MeetingConsumer,
} from "@videosdk.live/react-sdk";
import { authToken } from "../../util/VideoSDK";
import VideoMinutesRunOutNotification from "../../components/Dialogs/VideoMinutesRunOutNotfication";
import DcDialog from "../../components/Dialogs/DcDialog";
import { useDispatch, useSelector } from "react-redux";
import { setGameState } from "../../slice/gameStateSlice";
import {
  startConnectionWithGameCodeAndUserId,
} from "../../common/game/GameControl";
import { getOrCreatePlayerId } from "../../common/game/basic";
import { setPlayerId } from "../../slice/authSlice";
import { useDcPlayerId } from "../../hooks/useDcPlayerId";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";
import { useGameSignalR } from "../../hooks/useGameSignalR";
import { useVideoMeeting } from "../../hooks/useVideoMeeting";
import { useGameModalState } from "../../hooks/useGameModalState";
import SidebarCollapseToggle from "../../components/MainGameComponents/SidebarCollapseToggle";
import SidebarContent from "../../components/MainGameComponents/SidebarContent";

const MainGame = ({ isVideoChatAllowed = false }) => {
  const stateRef = useRef();
  const navigator = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 992;
  });

  // US-007-S1D: Video meeting state (not modal state - kept local until moved to video hook)
  const [isMeetingJoined] = useState(false);
  const [timerId, setTimerId] = useState(-1);
  const [totalMinutes, setTotalMinutes] = useState(0);

  // US-007-S1C/S1D: Modal visibility state from dedicated hook
  const {
    videoMinutesRunoutModalOpen,
    setVideoMinutesRunoutModalOpen,
    settlementModalEndGameOpen,
    setSettlementModalEndGameOpen,
    kickedModalOpen,
    setKickedModalOpen,
  } = useGameModalState();

  // redux states
  const user = useSelector((state) => state.auth.user);
  const asset = useSelector((state) => state.auth.asset);
  const gameState = useSelector((state) => state.gameState);
  const dispatch = useDispatch();
  const GameCode = searchParams.get("GameCode");
  const [connection, setConnection] = useState({});

  // Identity guard: detect mismatches between Redux and localStorage (DC-FE-IDENTITY-IMPL-001)
  const { mismatch: dcPlayerIdMismatch } = useDcPlayerId();

  // Sidebar collapse state (DC-UX-SIDEBAR-NO-PERSIST-DEFAULT-COLLAPSED-006)
  const [sidebarCollapsed, , toggleSidebarCollapsed] = useSidebarCollapsed();

  useEffect(() => {
    if (dcPlayerIdMismatch) {
      console.error(
        "[Main.js] dcPlayerId mismatch detected – Redux user.Id does not match localStorage.dcPlayerId"
      );
    }
  }, [dcPlayerIdMismatch]);

  useEffect(() => {
    document.body.classList.remove("public");
    return () => {
      document.body.classList.add("public");
    };
  });

  // TEMP: Runtime trace for turn changes (DC-CURRENT-TURN-RUNTIME-TRACE-001, DC-PLAYER-FLAGS-RUNTIME-TRACE-001)
  useEffect(() => {
    if (gameState.CurrentId) {
      const myPlayer = gameState.ActivePlayers?.find(p => p.PlayerId === user?.Id);
      console.log("=== TURN RUNTIME TRACE ===", {
        currentId: gameState.CurrentId,
        myUserId: user?.Id,
        myFlags: myPlayer ? { isFolded: myPlayer.IsFolded, isSitOut: myPlayer.IsSitOut, isDisconnected: myPlayer.IsDisconnected } : null,
        activePlayers: gameState.ActivePlayers?.map(p => ({ playerId: p.PlayerId, name: p.PlayerName, isFolded: p.IsFolded, isSitOut: p.IsSitOut, isDisconnected: p.IsDisconnected }))
      });
    }
  }, [gameState.CurrentId, user?.Id, gameState.ActivePlayers]);

  const preventrefresh = (e) => {
    e.preventDefault();
    e.returnValue = "data will get lost";
    return e.returnValue; // Return the value
  };

  useEffect(() => {
    window.addEventListener("beforeunload", preventrefresh);

    const handleResize = () => {
      if (typeof window === "undefined") return;
      setIsMobileViewport(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("beforeunload", preventrefresh);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  // start connection with per-browser playerId (not auth user.Id)
  useEffect(() => {
    // Guard: only require GameCode to join (guests allowed)
    if (!GameCode) {
      console.log("=== Main.js: no GameCode in URL ===");
      return;
    }

    // Use per-browser playerId for game identity (survives refresh, unique per browser)
    const playerId = getOrCreatePlayerId();
    // Store playerId in Redux so all components can find "me" in ActivePlayers
    dispatch(setPlayerId(playerId));
    // Use auth display name if available, fallback to "Guest" for anonymous users
    const displayName = user?.NickName || user?.DisplayName || "Guest";

    const startConnection = async () => {
      try {
        let newConnection = await startConnectionWithGameCodeAndUserId(
          GameCode,
          playerId
        );
        LogRocket.log("Connected to SignalR", newConnection.connectionId);
        setConnection(newConnection);
        SendRequest({
          method: "POST",
          url: "api/Games/JoinGame",
          data: {
            GameCode: GameCode,
            UserId: playerId,
            DisplayName: displayName,
            ConnectionId: newConnection.connectionId,
          },
        }).then((result) => {
          console.log("resultdat", result?.data);
          if (result.data === null || result.data === "") {
            alert(
              "Can't join game because of wrong game code, game is locked or didn't get invite"
            );
            navigator("/");
            return;
          }
          setIsLoading(false);
          dispatch(setGameState(result.data));
          LogRocket.log("Joined Game", result.data);
          // TEMP: Runtime trace for identity/turn debugging (DC-CURRENT-TURN-RUNTIME-TRACE-001, DC-PLAYER-FLAGS-RUNTIME-TRACE-001)
          const myPlayerJoin = result.data.ActivePlayers?.find(p => p.PlayerId === playerId);
          console.log("=== JOIN RUNTIME TRACE ===", {
            gameCode: GameCode,
            currentId: result.data.CurrentId,
            myUserId: playerId,
            myFlags: myPlayerJoin ? { isFolded: myPlayerJoin.IsFolded, isSitOut: myPlayerJoin.IsSitOut, isDisconnected: myPlayerJoin.IsDisconnected } : null,
            activePlayers: result.data.ActivePlayers?.map(p => ({ playerId: p.PlayerId, name: p.PlayerName, isFolded: p.IsFolded, isSitOut: p.IsSitOut, isDisconnected: p.IsDisconnected }))
          });
        }).catch((error) => {
          console.error("=== JoinGame/Rejoin error ===", error);
          // DC-FE-HOME-JOIN-MAINFAIL-TDD-GREEN-001: Navigate with error state instead of alert
          // US-008-S1G-MAIN-409-GREEN-008: Extract duplicate-name token from 409 response
          let joinApiError = "Failed to join game. The game may not exist or the server returned an error.";
          const status = error?.response?.status;
          const data = error?.response?.data;
          if (status === 409 && typeof data === "string" && data.includes("DUPLICATE_DISPLAY_NAME")) {
            joinApiError = "DUPLICATE_DISPLAY_NAME";
          }
          navigator("/", {
            state: {
              joinApiError,
            },
          });
        });
      } catch (err) {
        console.error("=== SignalR connection error ===", err);
        alert("Failed to connect to game server. Please try again.");
        navigator("/");
      }
    };
    startConnection();
    // RATIONALE: expanding deps causes re-join loops or blocks refresh/rejoin (see 2026-01-08 SignalR regression report)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GameCode]);

  //VideoSDK related function

  stateRef.current = {
    gameHash: gameState,
    timerId: timerId,
    asset: asset,
    minutes: totalMinutes,
    isCreator: gameState.GameCreatorId === user.Id,
  };

  // US-007-S1B: Extract video meeting logic to dedicated hook
  const meetingAPI = useVideoMeeting({
    stateRef,
    dispatch,
    setTimerId,
    setTotalMinutes,
    setVideoMinutesRunoutModalOpen,
    gameCode: GameCode,
    userId: user.Id,
  });

  // US-007-S1A: Extract SignalR handlers to dedicated hook
  useGameSignalR({
    connection,
    dispatch,
    stateRef,
    meetingAPI,
    navigator,
    isCreator: stateRef.current.isCreator,
    setKickedModalOpen,
    setSettlementModalEndGameOpen,
  });

  const renderDesktopSidebar = !sidebarCollapsed && !isMobileViewport;
  const renderMobilePanel = !sidebarCollapsed && isMobileViewport;

  if (!isLoading)
    return (
      <>
        {/* Game header: toggle button + join code (DC-UX-TABLE-TOGGLE-HEADER-JOINCODE-MOBILEPANEL-003) */}
        <div className="bg-black text-light p-2 d-flex align-items-center gap-3" style={{ borderBottom: "1px solid #333" }}>
          <SidebarCollapseToggle
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebarCollapsed}
          />
          <span style={{ fontSize: "0.9rem" }}>
            Join code: <strong>{GameCode}</strong>
          </span>
        </div>

        {/* Mobile in-flow panel: pushes content down (DC-UX-TABLE-MOBILE-INFLOW-ACCORDION-005) */}
        {renderMobilePanel && (
          <div className="dc-mobile-inflow-panel d-block d-lg-none">
            <div style={{ padding: "1rem" }}>
              <SidebarContent
                showHeadings
                settlementClassName=""
                gameState={gameState}
                settlementModalEndGameOpen={settlementModalEndGameOpen}
                setSettlementModalEndGameOpen={setSettlementModalEndGameOpen}
              />
            </div>
          </div>
        )}

        <div className="container-fluid bg-black p-0" id="GameBoard">
          <div className="row">
            {/* Desktop sidebar - only rendered when expanded (DC-UX-TABLE-TOGGLE-HEADER-JOINCODE-MOBILEPANEL-003) */}
            {renderDesktopSidebar && (
              <div className="mb-3 dc-sidebar-col col-lg-2 d-none d-lg-block">
                <div id="desktopSidebar">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "20px",
                    }}
                  >
                    <div
                      style={{
                        overflow: "auto",
                        width: "100%",
                      }}
                    >
                      <SidebarContent
                        settlementClassName="d-none d-lg-block"
                        gameState={gameState}
                        settlementModalEndGameOpen={settlementModalEndGameOpen}
                        setSettlementModalEndGameOpen={setSettlementModalEndGameOpen}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className={`dc-main-col${sidebarCollapsed ? " dc-main-col--expanded" : " col-lg-10"}`}>
              <div className="row">
                <div
                  className="logging d-none col-12 alert alert-warning"
                  id="logging"
                >
                  <span>Game Started</span>
                </div>
              </div>

              <div id="table">
                <div className="row">
                  <div
                    className="order-1 order-sm-0 col-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat"
                    data-dealer="2"
                  >
                    <Player ptr={2} dealerId={gameState.DealerId} />
                  </div>
                  <div
                    className="order-2 order-sm-0 col-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat"
                    data-dealer="3"
                  >
                    <Player ptr={3} dealerId={gameState.DealerId} />
                  </div>
                  <div className="order-0 order-sm-0 col-12 col-sm-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat">
                    {/* BetStatus strip above pot (DC-FE-SWAMP-UX-TRIAGE-001) */}
                    {gameState.BetStatus && (
                      <div className="text-center mb-2 p-1 bg-warning rounded" style={{fontSize: '0.9rem'}}>
                        {gameState.BetStatus}
                      </div>
                    )}
                    <div id="potdiv">
                      <PotDiv />
                    </div>
                  </div>
                  <div
                    className="order-3 col-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat"
                    data-dealer="4"
                  >
                    <Player ptr={4} dealerId={gameState.DealerId} />
                  </div>
                  <div
                    className="order-4 col-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat"
                    data-dealer="5"
                  >
                    <Player ptr={5} dealerId={gameState.DealerId} />
                  </div>
                  <div
                    className="order-5 col-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat"
                    data-dealer="6"
                  >
                    <Player ptr={6} dealerId={gameState.DealerId} />
                  </div>
                  {/* {activePlayers.slice(0, middleIndex).map((player, index) => (
                    <div key={player.PlayerId} className={`order-1 order-sm-${index} col-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat`} data-ptr={player.ptr}>
                      <Player ptr={player.ptr} dealerId={gameState.DealerId} />
                    </div>
                  ))} */}

                  {/* POT DIV - Always in Middle */}
                  {/* <div className={`order-0 order-sm-${middleIndex} col-12 col-sm-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat`}>
                    <div id="potdiv">
                      <PotDiv />
                    </div>
                  </div>

                  {activePlayers.slice(middleIndex).map((player, index) => (
                    <div key={player.PlayerId} className={`order-${middleIndex + index + 1} col-6 col-md-3 col-lg-2 mt-2 mt-md-1 mt-lg-0 seat`} data-ptr={player.ptr}>
                      <Player ptr={player.ptr} dealerId={gameState.DealerId} />
                    </div>
                  ))} */}
                </div>
                <div className="row mt-3">
                  <div className="col-12 col-md-12">
                    <CurrentPlayerDiv />
                  </div>
                  {/* <div className="col-12 col-md-2">
                    <DefaultPlayer ptr={1} />
                  </div> */}
                </div>
              </div>
              <div className="player-deal-card my-3 overflow-hidden">
                {gameState.DealerId === user.Id && <DealerPanel />}
              </div>
              <GameControlPanel isMeetingJoined={isMeetingJoined} />
            </div>
          </div>
        </div>
        <VideoMinutesRunOutNotification
          open={videoMinutesRunoutModalOpen}
          setOpen={setVideoMinutesRunoutModalOpen}
        />
        <ToastContainer />
        {/* DC-FE-PLAYERLEFT-TOAST-AND-KICKED-MODAL-001: Kicked modal */}
        <DcDialog
          isOpen={kickedModalOpen}
          title="Removed from Game"
          body="You have been removed from this game by the host."
          confirmLabel="OK"
          cancelLabel=""
          variant="info"
          onConfirm={() => {
            setKickedModalOpen(false);
            navigator("/");
          }}
          onCancel={() => {
            setKickedModalOpen(false);
            navigator("/");
          }}
        />
        {/* <SettlementModalEndGame
          isShow={false}
          gameState={gameState}
          open={settlementModalEndGameOpen}
          setOpen={setSettlementModalEndGameOpen}
        /> */}
      </>
    );
  else return <h1>Loading...</h1>;
};

const MeetingProviderWrappedMainGame = () => {
  const user = useSelector((state) => state.auth.user);
  const [searchParams] = useSearchParams();
  const meetingId = searchParams.get("MeetingId");

  if (meetingId === null || authToken === null) {
    return <MainGame isVideoChatAllowed={false} />;
  } else {
    return (
      authToken && (
        <MeetingProvider
          config={{
            meetingId,
            micEnabled: true,
            webcamEnabled: true,
            name: user.DisplayName,
            participantId: user.Id,
          }}
          token={authToken}
        >
          <MeetingConsumer>
            {() => <MainGame isVideoChatAllowed={true} />}
          </MeetingConsumer>
        </MeetingProvider>
      )
    );
  }
};

export default MeetingProviderWrappedMainGame;
